import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import CardDropdown from "components/common/CardDropdown";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState, useMemo } from "react";
import { Dropdown, Row, Col, Spinner } from "react-bootstrap";
import { Input } from "antd";
import ViewFeedbackModal from "./ViewFeedbackModal";
import { toast } from "react-toastify";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import TablePaginationFooter from "components/common/TablePaginationFooter";

const FEELING_OPTIONS = [
  { key: "LIKE", label: "\u{1F44D} LIKE" },
  { key: "DISLIKE", label: "\u{1F44E} DISLIKE" },
  { key: "HAPPY", label: "\u{1F60A} HAPPY" },
  { key: "SAD", label: "\u{1F614} SAD" },
];

export default function FeedbacksTable({
  feedbacks,
  loading,
  getFeedback,
  deleteFeedback,
  page,
  pageSize,
  setPage,
  setPageSize,
  totalPages,
  totalElements,
  searchTerm,
  setSearchTerm,
}) {
  const [isFetchingItem, setIsFetchingItem] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const formatText = (text, end) => (text.length > end ? `${text.slice(0, end)}...` : text);
  
  const FEELING_MAP = useMemo(() => 
    FEELING_OPTIONS.reduce((acc, item) => ({ ...acc, [item.key]: item.label }), {}), 
  []);

  const handleViewFeedback = async (id) => {
    setShowModal(true);
    setIsFetchingItem(true);
    try {
      const res = await getFeedback(id);
      setSelectedFeedback(res);
    } catch (error) {
      toast.error(error?.message || "Error fetching feedback", { theme: "colored" });
    } finally {
      setIsFetchingItem(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteFeedback(feedbackToDelete);
      setShowDeleteModal(false);
      toast.success("Feedback deleted successfully");
    } catch (error) {
      console.log(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: "userId",
      header: "User",
      meta: { headerProps: { className: "text-900 py-2" }, cellProps: { className: "py-2" } },
      cell: ({ getValue }) => {
        const text = getValue();
        return <span className="text-800 fw-medium fs-10">{text ? formatText(text, 12) : "None"}</span>;
      },
    },
    {
      accessorKey: "subject",
      header: "Subject",
      meta: { headerProps: { className: "text-900 py-2" }, cellProps: { className: "py-2" } },
      cell: ({ getValue }) => <h6 className="mb-0 text-900 fs-10">{formatText(getValue() || "No Subject", 25)}</h6>,
    },
    {
      accessorKey: "feeling",
      header: "Feeling",
      meta: { headerProps: { className: "text-900 py-2" }, cellProps: { className: "py-2" } },
      cell: ({ getValue }) => {
        const value = getValue();
        const label = FEELING_MAP[value] || value || "—";
        return (
          <span className="badge rounded-pill fw-bold" style={{ 
            backgroundColor: value ? "#eefaf0" : "#fff1f0", 
            color: value ? "#1e7e34" : "#cf1322",
            fontSize: '0.65rem'
          }}>
            {label}
          </span>
        );
      },
    },
    {
      accessorKey: "content",
      header: "Content",
      meta: { headerProps: { className: "text-900 py-2" }, cellProps: { className: "py-2" } },
      cell: ({ getValue }) => <span className="text-700 fs-10">{formatText(getValue() || "No Content", 35)}</span>,
    },
    {
      accessorKey: "attachments",
      header: "Files",
      meta: { headerProps: { className: "text-900 text-center py-2" }, cellProps: { className: "text-center py-2" } },
      cell: ({ getValue }) => (
        <span className="text-900 fs-10">
          <FontAwesomeIcon icon="paperclip" className="me-1 opacity-50" />
          {getValue()?.length || 0}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      meta: { headerProps: { className: "text-900 text-center py-2" }, cellProps: { className: "text-center py-2" } },
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Guard privilege={PRIV.VIEW_FEEDBACKS}>
                <Dropdown.Item onClick={() => handleViewFeedback(item.id)}>View Details</Dropdown.Item>
              </Guard>
              <Guard privilege={PRIV.DELETE_FEEDBACKS}>
                <Dropdown.Divider as="div" />
                <Dropdown.Item onClick={() => { setFeedbackToDelete(item.id); setShowDeleteModal(true); }} className="text-danger">Delete</Dropdown.Item>
              </Guard>
            </div>
          </CardDropdown>
        );
      },
    },
  ], [FEELING_MAP]);

  const table = useAdvanceTable({
    data: feedbacks,
    columns,
    sortable: true,
    pagination: false,
  });

  return (
    <>
      {/* ================= SEARCH + RESULTS ROW ================= */}
      <Row className="mb-2 g-3 align-items-center">
        <Col md={8} lg={9}>
          <div className="d-flex align-items-center gap-3">
            <Input
              prefix={<span style={{ fontSize: 14 }}>🔍</span>}
              placeholder="Search feedbacks..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              allowClear
              size="large"
              style={{ borderRadius: 12, height: 30, maxWidth: 300 }}
            />
          </div>
        </Col>
        <Col md={4} lg={3} className="text-end">
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Results: <span className="fw-semibold text-dark">{feedbacks.length}</span> of <span className="fw-semibold text-dark">{totalElements}</span>
          </div>
        </Col>
      </Row>

      {/* ================= CENTERED LOADER ================= */}
      {loading && (!feedbacks || feedbacks.length === 0) ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 240 }}>
          <Spinner animation="border" variant="success" />
        </div>
      ) : (
        <AdvanceTableProvider {...table}>
          <AdvanceTable
            headerClassName="bg-light text-nowrap align-middle text-dark"
            rowClassName="align-middle white-space-nowrap"
            tableProps={{
              bordered: true,
              striped: true,
              className: "fs-10 mb-0 overflow-hidden",
            }}
          />
          <div className="mt-3">
            <TablePaginationFooter
              page={page}
              totalPages={totalPages || 0}
              totalElements={totalElements || 0}
              onPrev={page > 0 ? () => setPage((p) => p - 1) : undefined}
              onNext={page < totalPages - 1 ? () => setPage((p) => p + 1) : undefined}
              pageSize={pageSize}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              pageSizeOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </AdvanceTableProvider>
      )}

      {/* ================= MODALS ================= */}
      <ViewFeedbackModal
        show={showModal}
        onClose={() => { setShowModal(false); setSelectedFeedback(null); }}
        feedback={selectedFeedback}
        loading={isFetchingItem}
      />

      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        itemName="feedback"
        loading={isDeleting}
      />
    </>
  );
}